<template>
  <b-modal
    :visible="show"
    centered
    title="Import CSV"
    modal-class="plant-csv-import-modal"
    hide-footer
    no-close-on-backdrop
    @change="onVisibilityChange"
    @hidden="onHidden"
  >
    <b-form @submit.prevent="submit">
      <b-form-group label="File" class="csv-import-field">
        <b-form-file
          v-model="file"
          accept=".csv,.xlsx"
          placeholder="Choose a .csv or .xlsx file..."
          drop-placeholder="Drop file here..."
        />
      </b-form-group>

      <hr class="csv-import-divider" />

      <b-form-group label="Project scope" class="csv-import-field mb-0">
        <small class="csv-import-help">
          Project scope for every item in this file is set here, and only here.
          Nothing in the file itself changes it.
        </small>
        <b-form-radio-group
          v-model="projectScope"
          class="csv-import-scope"
          stacked
          required
          :options="scopeOptions"
        />
        <project-access-selector
          v-model="projectIds"
          :disabled="projectScope === 'all'"
        />
      </b-form-group>

      <div class="csv-import-actions">
        <b-button variant="invisible" @click="close">Cancel</b-button>
        <b-button
          type="submit"
          variant="primary"
          :disabled="
            !file ||
            importing ||
            !projectScope ||
            (projectScope === 'specific' && projectIds.length === 0)
          "
        >
          <b-spinner v-if="importing" small class="mr-1" />
          Import
        </b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ProjectAccessSelector from '@/components/forms/ProjectAccessSelector.vue'

const MAX_CSV_SIZE_BYTES = 5 * 1024 * 1024

export default {
  name: 'PlantEquipmentCsvImportModal',

  components: { ProjectAccessSelector },

  props: {
    show: { type: Boolean, default: false },
  },

  data() {
    return {
      file: null,
      // null so neither option is preselected — the import button stays
      // disabled until the BA deliberately picks a scope.
      projectScope: null,
      projectIds: [],
      importing: false,
    }
  },

  computed: {
    ...mapGetters('auth', ['isProjectScoped']),

    /**
     * All-projects is the widest scope in the system — a zero-pivot item shows
     * up on every project in the company. Only a company-wide admin may choose
     * it, so a project-scoped BA never sees the option. The API enforces this
     * independently (422); hiding it here just avoids offering a button that
     * can only fail.
     */
    scopeOptions() {
      const options = [
        { value: 'specific', text: 'Only the projects I pick below' },
      ]

      if (!this.isProjectScoped) {
        options.push({ value: 'all', text: 'All projects' })
      }

      return options
    },
  },

  watch: {
    show(open) {
      if (open) {
        this.file = null
        this.projectScope = null
        this.projectIds = []
      }
    },
  },

  methods: {
    ...mapActions('plantEquipment', ['importPlantEquipmentCsv']),

    async submit() {
      if (!this.file) return

      const validExt = /\.(csv|xlsx)$/i.test(this.file.name)
      if (!validExt) {
        this.$bvToast.toast('Please select a .csv or .xlsx file.', {
          title: 'Invalid file',
          variant: 'danger',
          solid: true,
        })
        return
      }
      if (this.file.size > MAX_CSV_SIZE_BYTES) {
        this.$bvToast.toast('File is too large. Maximum size is 5MB.', {
          title: 'Invalid file',
          variant: 'danger',
          solid: true,
        })
        return
      }

      this.importing = true
      try {
        const applyToAll = this.projectScope === 'all'
        const result = await this.importPlantEquipmentCsv({
          file: this.file,
          projectIds: applyToAll ? [] : this.projectIds,
          applyToAll,
        })
        this.$emit('imported', result)
        this.close()
      } catch (err) {
        const message =
          (err.response && err.response.data && err.response.data.message) ||
          'Failed to import CSV. Please try again.'
        this.$bvToast.toast(message, {
          title: 'Import failed',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.importing = false
      }
    },

    onVisibilityChange(visible) {
      if (!visible) this.$emit('update:show', false)
    },

    close() {
      this.$emit('update:show', false)
    },

    onHidden() {
      this.$emit('update:show', false)
    },
  },
}
</script>

<style lang="scss" scoped>
.csv-import-field {
  margin-bottom: 16px;
}

.csv-import-field ::v-deep legend,
.csv-import-field ::v-deep label {
  padding: 0;
  margin-bottom: 7px;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
}

.csv-import-divider {
  margin: 22px 0 20px;
  border-color: #e5eaf2;
}

.csv-import-help {
  display: block;
  margin: -1px 0 10px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.4;
}

.csv-import-scope ::v-deep .custom-control-label {
  padding-left: 8px;
}

.csv-import-scope {
  margin-bottom: 12px;
}

.csv-import-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 16px;
  margin-top: 24px;
  border-top: 1px solid #e5eaf2;
}

.csv-import-actions .btn {
  min-width: 112px;
  min-height: 42px;
  border-radius: 8px;
}
</style>
